import React from "react";
import { useTranslation } from "react-i18next";
import { Work, WorkMetric } from "../../types/works";

/**
 * Renders the "Universal Metrics Framework": every Work tracks
 * Tonnage · Distance/Km · Hours · Services. Tonnage/distance/hours show as
 * planned-vs-actual pairs; services shows a plain count (the lines themselves
 * are listed separately). Metrics with neither value are shown as "—".
 */

const formatNumber = (value: number) => {
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(1).replace(/\.0$/, "");
};

interface MetricCellProps {
  label: string;
  metric: WorkMetric | null | undefined;
  unit: string;
}

const MetricCell: React.FC<MetricCellProps> = ({ label, metric, unit }) => {
  const { t } = useTranslation("works");
  const hasPlanned = metric?.planned !== undefined && metric?.planned !== null;
  const hasActual = metric?.actual !== undefined && metric?.actual !== null;

  if (!hasPlanned && !hasActual) {
    return (
      <div>
        <div className="text-xs text-[#8A9590] mb-1">{label}</div>
        <div className="text-sm text-[#8A9590]">—</div>
      </div>
    );
  }

  const pct =
    hasPlanned && hasActual && metric!.planned! > 0
      ? Math.min(100, Math.round((metric!.actual! / metric!.planned!) * 100))
      : null;

  // Actual beyond planned isn't progress — surface the overage rather than
  // showing the confusing "4 / 2" fraction on its own.
  const over = hasPlanned && hasActual && metric!.actual! > metric!.planned!;

  return (
    <div>
      <div className="text-xs text-[#8A9590] mb-1">{label}</div>
      <div className="text-sm font-medium">
        <span className={over ? "text-[#C97A3C]" : "text-[#2B2F2E]"}>
          {hasActual ? formatNumber(metric!.actual!) : "0"}
        </span>
        {hasPlanned && (
          <span className="text-[#8A9590] font-normal"> / {formatNumber(metric!.planned!)}</span>
        )}
        <span className="text-[#8A9590] font-normal text-xs ml-1">{unit}</span>
        {over && (
          <span
            className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FCEEE2] text-[#C97A3C]"
            title={t("metrics.overPlan", { n: formatNumber(metric!.actual! - metric!.planned!) })}
          >
            {t("metrics.overBadge", { n: formatNumber(metric!.actual! - metric!.planned!) })}
          </span>
        )}
      </div>
      {pct !== null && (
        <div className="mt-1.5 h-1.5 w-full rounded-full bg-[#F0EFEB] overflow-hidden">
          <div
            className={`h-full rounded-full ${over ? "bg-[#E2A04B]" : "bg-[#C97A3C]"}`}
            style={{ width: `${pct}%` }}
            title={t("metrics.progressTooltip", { pct })}
          />
        </div>
      )}
    </div>
  );
};

interface MetricsGridProps {
  work: Pick<Work, "tonnage" | "distanceKm" | "hours" | "services">;
  className?: string;
}

export const MetricsGrid: React.FC<MetricsGridProps> = ({ work, className = "" }) => {
  const { t } = useTranslation("works");

  return (
    <div className={`grid grid-cols-2 sm:grid-cols-4 gap-4 ${className}`}>
      <MetricCell label={t("metrics.tonnage")} metric={work.tonnage} unit="t" />
      <MetricCell label={t("metrics.distance")} metric={work.distanceKm} unit="km" />
      <MetricCell label={t("metrics.hours")} metric={work.hours} unit="h" />
      {/* Services: a plain total count — the individual lines are listed below. */}
      <div>
        <div className="text-xs text-[#8A9590] mb-1">{t("metrics.services")}</div>
        <div className="text-sm font-medium text-[#2B2F2E]">
          {work.services.length}
        </div>
      </div>
    </div>
  );
};
